// libraries
import { Component, Inject, OnInit, HostListener, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { FormControl, NgForm } from '@angular/forms';

// moment
import { Moment } from 'moment';
import * as _moment from 'moment';

// reactivity
import { Observable, concat, of, Subject, BehaviorSubject } from 'rxjs';
import { tap, debounceTime, distinctUntilChanged, switchMap, map, finalize, take, filter, first } from 'rxjs/operators';

// application custom services
import { RecordService } from '../../../../../services/record.service';
import { NotificationService } from '../../../../../services/notification.service';
import { CustomTranslateService } from '../../../../../services/custom-translate.service';
import { ViewService } from '../../../../../services/view.service';
import { LocalStorageService } from '../../../../../services/local-storage.service';
import { NumberService } from '../../../../../services/helpers/number.service';

// application custom class
import { Select } from '../../../../../objects/select';
import { StatusCode } from '../../../../../lists/status-code';

// application contracts
import { IDialogData, ICustomerOption, ISiteOption, IContactOption,
  IDepartmentOption, ITaxCodeOption, IAccountCodeOption, IPeriodOption,
  IDatePeriodOption, ILineItem, IAssignedUserOption, IItemOption,
  IRelatedModuleData, IPaymentTermOption, IContractOption
} from '../../../../../contracts/recurring-invoice';

import { isEmpty, get, toNumber, toString, find, first as _first, cloneDeep, clone } from 'lodash-es';
import { ClientStoreService } from '../../../../../services/client-store.service';
import { LooseObject } from '../../../../../objects/loose-object';
import { toFormattedNumber } from '../../../../utils/numbers';
import { blank, filled } from '../../../../utils/common';
import { Relate } from '../../../../../objects/relate';
import { ContextMenuComponent } from '../../../context-menu/context-menu.component';
import { ContextMenuService } from '../../../../../services/context-menu.service';
import { UUID } from 'angular2-uuid';
import { RecurringInvoiceLine } from '../../../../../objects/line-items/recurring-invoice-line';
import { EditformComponent } from '../../../editform/editform.component';
import { FormPopup } from '../../../../../objects/centralized-forms/form-popup';
import { AIInvoiceService } from '../../../../../services/ai-invoice.service';
import { GptPromptService, GptPrompt } from '../../../../../services/gpt-prompt.service';
import { Liquid } from 'liquidjs';

// create moment instance
const moment = (_moment as any).default || _moment;

@Component({
  selector: 'app-edit-recurring-invoice-form',
  templateUrl: './edit-recurring_invoice-form.component.html',
  styleUrls: ['./edit-recurring_invoice-form.component.scss'],
})
export class EditRecurringInvoiceFormComponent implements OnInit {
  @ViewChild(ContextMenuComponent) contextMenuComponent: ContextMenuComponent;
  /**
   * sets the current recurring invoice identifier
   *
   * @var {string}
   */
  strCurrentRecordID: string;

  /**
   * contains the information regarding the current recurring invoice date
   *
   * @var {RecurringInvoiceData}
   */
  objRecurringInvoice: RecurringInvoiceData = {
    strId: null,
    isAutomatic: false,
    isActive: true,
    emailToClient: false,
    objPeriod: null,
    objNextInvoiceDate: new moment(),
    objCustomer: null,
    objContact: null,
    objSite: null,
    objExpiryDate: null,
    strPurchaseOrderNumber: null,
    objInvoiceDate: null,
    strInvoiceSummary: null,
    objAssignedUser: null,
    strReference:  null,
    objPaymentTerm: null,
    repeated_every: null,
    objContract: null,
  };

  // Initialize Liquid.js engine for resolving variables in AI prompts
  private liquidEngine = new Liquid();

  /**
   * tells if the current form is submitting
   *
   * @var {boolean}
   */
  isSubmitting: boolean = false;

  /**
   * tells if the current related field list is fetching to
   * the api and updating
   *
   * @var {object}
   */
  isLoadingRelated = {
    customers: false,
    sites: false,
    users: false,
  };

  /**
   * contains the list of related customers as obversable
   * property for select input field which is using the
   * ng-select
   *
   * @var {Observable<Select[]>}
   */
  arSelectableRelatedCustomers$: Observable<Select[]>;

  /**
   * Contains the search subject for customer field
   *
   * @type {Subject<string>}
   */
  objSearchCustomerInput$ = new Subject<string>();

  /**
   * contains the list of selectable related sites as observable
   * property for select input field which is using the
   * ng-selectngModelGroup="arLineItems"
   *
   * @var {Observable<Select[]>}
   */
  arSelectableRelatedSites$: Observable<Select[]>;

  /**
   * contains the list of selectable related sites as observable
   * property for select input field which is using the
   * ng-selectngModelGroup="arLineItems"
   *
   * @var {Subject<string>}
   */
  arSelectableRelatedSitesInput$ = new Subject<string>();

  /**
   * tells if the current related sites lists if fetching from the api
   * and updating its content
   *
   * @var {boolean}
   */
  isLoadingRelatedSites: boolean = false;

  /**
   * contains the list of selected related contacts as observable
   * property which the combination of the customer contacts
   * and site contacts. If data is missing from both ends it will be the default
   * contacts for the current client
   *
   * @var {Observable<Select[]>}
   */
  arSelectableRelatedContacts$: Observable<Select[]>;

  /**
   * observable property which will contain the string of search
   * from the typeahead function of the select field
   *
   * @var {Subject<string>}
   */
  objRelatedContactInput$: Subject<string> = new Subject<string>();

  /**
   * tells if the current related contacts list is fetching from the api
   * and updating its content
   *
   * @var {boolean}
   */
  isLoadingRelatedContacts: boolean = false;

  /**
   * contains the list of selectable related users as observable property
   * for select input field which is using the ng-selectngModelGroup="arLineItems"
   *
   * @var {Observable<Select[]>}
   */
  arSelectableAssignedUser$: Observable<Select[]>;

  /**
   * observable property which will contain the string of search
   * from the typeahead function of the select field
   *
   * @var {Subject<string>}
   */
  objRelatedAssignedUserInput$: Subject<string> = new Subject<string>();

  /**
   * tells if the current related assigned users is being fetched from the api and updating
   * its content
   *
   * @var {boolean}
   */
  isLoadingRelatedAssignedUsers: boolean = false;

   /**
   * contains the list of selectable related payment terms as observable
   * property for select input field
   *
   * @var {Observable<Select[]>}
   */
  arSelectableRelatedPaymentTerms$: Observable<Select[]>;

  /**
   * contains the list of selectable related payment terms as observable
   * property for select input field which is using the
   * ng-selectngModelGroup="arLineItems"
   *
   * @var {Subject<string>}
   */
  arSelectableRelatedPaymentTermsInput$ = new Subject<string>();

  /**
   * tells if the current related payment term lists if fetching from the api
   * and updating its content
   *
   * @var {boolean}
   */
  isLoadingRelatedPaymentTerms: boolean = false;

  /**
   * contains the selectable periods for the select field period
   *
   * @var {Array<Select>}
   */
  arSelectablePeriods$: Observable<IPeriodOption[]> = of([
    { id: 'months_1', text: 'monthly' },
    { id: 'months_3', text: 'quarterly' },
    { id: 'months_6', text: 'bi_annual' },
    { id: 'years_1', text: 'annual' },
    { id: 'years_2', text: 'every_2_years' },
    { id: 'years_3', text: 'every_3_years' },
    { id: 'years_4', text: 'every_4_years' },
    { id: 'years_5', text: 'every_5_years' }
  ]);

  public arFrequencyList = [
    {id: 'day', text: 'day'},
    {id: 'week', text: 'week'},
    {id: 'month', text: 'month'},
    {id: 'year', text: 'year'}
  ];

  strFrequency: string;
  strQuantity: string;

  strRepeatedEvery: string;

  /**
   * contains the selectable date periods for invoice date and due date
   * field
   *
   * @var {Array<Select>}
   */
  arSelectableDatePeriods$: Observable<IDatePeriodOption[]> = of([
    { id: 'today', text: 'today' },
    { id: 'days_7', text: 'seven_days' },
    { id: 'days_14', text: 'fourteen_days' },
    { id: 'days_30', text: 'thirty_days' },
    { id: 'last_day_of_this_month', text: 'last_day_of_this_month' },
    { id: 'first_day_of_next_month', text: 'first_day_of_next_month' },
  ]);

  /**
   * tells if the current line items data are successfully loaded
   *
   * @var {boolean}
   */
  isLineItemsLoaded: boolean = false;

  /**
   * contains the total exempted tax
   *
   * @var {number}
   */
  fltTotalExcludedTaxAmount: number = 0;

  /**
   * contains the total taxed amount
   *
   * @var {number}
   */
  fltTotalTaxAmount: number = 0;

  /**
   * contains the total included tax amount
   *
   * @var {number}
   */
  fltTotalIncludedTaxAmount: number = 0;

  /// this contains the tax adjustment for the whole invoice
  totalTaxAdjustment: number = 0;

  focusedLineIndex: number = -1;

  arLineItems: ILineItem[] = [];

  /**
   * Current form reference
   *
   * @type {NgForm}
   */
  @ViewChild('recurringInvoiceForm') recurringInvoiceForm: NgForm;

  /**
   * Listen to escape press of the user
   *
   * @returns {void}
   */
  @HostListener('window:keyup.esc')
  onKeyUp(): void {
    this.closeDialog();
  }

  /**
   * Flag used to check if the current component has a reference of the form
   *
   * @type {boolean}
   */
  get hasFormReference(): boolean {
    return !! this.recurringInvoiceForm;
  }

  /**
   * Observable to emit when disabling the submit button
   *
   * @type {BehaviorSubject<boolean>}
   */
  disabledSaveButton$ = new BehaviorSubject(false);

  /**
   * Flag to show progress state for AI generation
   */
  whenInProgress$ = new BehaviorSubject<boolean>(false);

  /**
   * Configured default account code (only present with accounting connected clients)
   *
   * @type {IAccountCodeOption}
   */
  objDefaultAccountCode: IAccountCodeOption;

  /**
   * Configured default tax code (only present with accounting connected clients)
   *
   * @type {ITaxCodeOption}
   */
  objDefaultTaxCode: ITaxCodeOption;

  // 4611
  /**
   * Configured default department
   *
   * @type {IDepartmentOption}
   */
  objDefaultDepartment: IDepartmentOption;

  /**
   * @type {IDepartmentOption[]}
   */
  protected arCachedDepartments: IDepartmentOption[] = [];

  /**
   * @type {ITaxCodeOption[]}
   */
  protected arCachedTaxCodes: ITaxCodeOption[] = [];

  /**
   * @type {IAccountCodeOption[]}
   */
  protected arCachedAccountCodes: IAccountCodeOption[] = [];

  /**
   * Configured default customer payment terms
   *
   * @type {IPaymentTermOption[]}
   */
  objDefaultPaymentTerm: IPaymentTermOption;

  /**
   * If department tracking is enabled.
   *
   * @var {boolean}
   */
  public bDepartmentTracking: boolean = false;
  public selectedLineItems: Array<LooseObject> = [];
  // AI Prompt selector state for recurring invoices
  public showPromptSelector: boolean = false; // controls visibility of the prompt picker
  public arAvailablePrompts: GptPrompt[] = []; // available prompts from AI Prompt Management
  public objSelectedPrompt: GptPrompt | null = null; // selected prompt

  objContractRelate = new Relate<Select[]>();

  get bFromDuplicate(): boolean {
    return get(this.objData, 'action') === 'duplicate'
  }

  /**
   * class constructor
   *
   * @param {MatDialogRef<EditRecurringInvoiceFormComponent>} currentDialog
   * @param {RecordService}                                   recordService
   * @param {NotificationService}                             notificationService
   * @param {CustomTranslateService}                          translationService
   * @param {ViewService}                                     viewService
   * @param {MAT_DIALOG_DATA: IDialogData}                    objData
   */
  constructor(
    public contextMenuService: ContextMenuService,
    protected currentDialog: MatDialogRef<EditRecurringInvoiceFormComponent>,
    protected recordService: RecordService,
    protected notificationService: NotificationService,
    protected translationService: CustomTranslateService,
    protected viewService: ViewService,
    protected localStorage: LocalStorageService,
    @Inject(MAT_DIALOG_DATA) protected objData: IDialogData,
    protected numberService: NumberService,
    protected clientService: ClientStoreService,
    protected dialog: MatDialog,
    private gptPromptService: GptPromptService,
    private aiInvoiceService: AIInvoiceService,
  ) {}

  /**
   * {@inheritdoc}
   *
   * angular lifecycle callback called when template was initialized
   */
  ngOnInit(): void {
    this.bDepartmentTracking = this.clientService.isDepartmentTracking();
    this.recordService.getRecordBasedOnParent(this.getData('moduleName') === 'sites' || this.getData('moduleName') === 'customers' || this.getData('moduleName') === 'payment_terms')
    .subscribe( response => {
      if (this.getData('moduleName') === 'sites') {
        this.initSiteModuleRecurringInvoice(response);
      } else if (this.getData('moduleName') === 'customers') {
        this.initCustomerModuleRecurringInvoice(response);
      } else if (this.getData('moduleName') === 'payment_terms') {
        this.initCustomerModuleRecurringInvoice(response);
      } else {
        if (this.isCreateForm() || this.bFromDuplicate) {
          this.setCurrentSite(null);
          this.setCurrentPaymentTerm(null);

          if (this.objData['customer_id'] && this.objData['customer_text']) {
            this.setCurrentCustomer({
              id: this.objData['customer_id'],
              text: this.objData['customer_text']
            });
          } else {
            this.setCurrentCustomer(null);
          }

          if (filled(this.objData['contract_id']) && filled(this.objData['contract_text'])) {
            this.setCurrentContract({
              id: this.objData['contract_id'],
              text: this.objData['contract_text']
            });
          }

          if (filled(this.objData['invoice_date_id']) && filled(this.objData['invoice_date_text'])) {
            this.setSelectedInvoiceDate({
              id: this.objData['invoice_date_id'],
              text: this.objData['invoice_date_text']
            })
          }

          if (filled(this.objData['next_invoice_date'])) {
            this.objRecurringInvoice.objNextInvoiceDate = this.objData['next_invoice_date']
          }

          this.recordService.getRecordConfig('recurring_invoices', {}, true).subscribe((config) => {
            this.setRelatedDefaults(config['related_data']);

            this.initSelectableFieldsValues();

            this.setSelectableDepartments(config['related_data']['departments'] || []);

            this.setSelectableTaxCodes(config['related_data']['tax_codes'] || []);

            this.setSelectableAccountCodes(config['related_data']['account_codes'] || []);

            const details = get(config, 'record_details', {});
            const user = Object.assign({}, {
                ... (toString(get(details, 'user_id')).trim().length > 0 && {
                  id: details['user_id'],
                }),
                ... (toString(get(details, 'user_text')).trim().length > 0 && {
                  text: details['user_text'],
                }),
              });

            if (! isEmpty(user)) {
              this.objRecurringInvoice.objAssignedUser = user;
            }

            this.setCurrentPaymentTerm(this.objDefaultPaymentTerm);

            if (this.bFromDuplicate) {
              config['record_details'] = this.objData.objDefault
              this.initRecurringInvoiceForm(config);
            }
          });
        } else {
          this.setCurrentRecordID(this.getData('recordID'));
          this.initEditRecurringInvoiceForm();
        }
      }
    })

    // as per elle translations should be initialized
    this.getTranslationService().initializeTransalateables([ 'confirm_discard', 'confirm_discard_header' ]);

    this.currentDialog.backdropClick().subscribe(_ => {
      this.closeDialog();
    });
  }

  /**
   * pushes a new stack of line item to the list
   *
   * @returns {void}
   */
  addLineItem(): void {
    this.addEmptyLineItem();
  }

  /**
   * called by the close/cancel button for closing the current instance
   * of the dialog
   */
  closeDialog(): void {
    // if current form was changed
    if (this.hasFormReference && this.recurringInvoiceForm.dirty) {
      // show notification
      this.getNotificationService().sendConfirmation(
        'confirm_discard',
        'confirm_discard_header'
      ).subscribe(confirmation => {
        if (confirmation.answer) {
          this.currentDialog.close();
        }
      });
    } else {
      this.currentDialog.close();
    }
  }

  /**
   * calculates the line item amount
   *
   * @param   {any} objLineItem
   *
   * @returns {void}
   */
  calculateAmount(objLineItem: ILineItem): void {
    let intQuantity: any = objLineItem.quantity || 0;
    let fltUnitPrice: any = objLineItem.unitPrice || 0;

    objLineItem.unitPrice = toFormattedNumber(parseFloat(fltUnitPrice.toString()), {
      currency: true,
      maxDecimalPlaces: 4,
    });
    objLineItem.amount = toFormattedNumber(parseFloat((intQuantity * fltUnitPrice).toString()), {
      currency: true,
    });

    // recalculate totals
    this.calculateLineItemsTotal();
  }

  /**
   * calculates the total line items amount which includes the total excluded tax amount,
   * total taxable amount and total amount including the tax
   *
   * @returns {void}
   */
  calculateLineItemsTotal(): void {
    // reset calculations
    let fltTotalTaxAmount: number = 0;
    let fltTotalExcludedTaxAmount: number = 0;

    // loop each line items
    this.arLineItems.forEach((objLineItem) => {
      // calculate excluded tax
      fltTotalExcludedTaxAmount += (toNumber(objLineItem.unitPrice) * objLineItem.quantity);
      // calculate total taxable amount
      if (objLineItem.objTaxCode) {
        const taxRate = toNumber(objLineItem.objTaxCode.rate) / 100;
        const tax = toNumber(objLineItem.amount) * taxRate;

        fltTotalTaxAmount += tax;
      }
    });

    // sets the property values
    this.fltTotalExcludedTaxAmount = toFormattedNumber(fltTotalExcludedTaxAmount, {
      currency: true,
    });

    this.fltTotalTaxAmount = toFormattedNumber(fltTotalTaxAmount, {
      currency: true,
    });

    // calculate total inc tax amount
    this.fltTotalIncludedTaxAmount = this.fltTotalExcludedTaxAmount + (this.fltTotalTaxAmount + toNumber(this.totalTaxAdjustment));
  }

  /**
   * checks if the current dialog is a create form or edit form
   *
   * @returns {boolean}
   */
  isCreateForm(): boolean {
    return (this.getData('isNew') === true);
  }

  /**
   * removes a line item from the line items list based on given
   * index and recalculates the total
   *
   * @param   {number} intIndex
   *
   * @returns {void}
   */
  removeLineItem(intIndex: number): void {
    // do nothing
    if (intIndex < 0 || this.arLineItems.length <= 1) {
      return;
    }

    // verify we dont exceed to the array bounds of line items
    if (this.arLineItems.length > intIndex) {
      // destroy selected row
      this.arLineItems.splice(intIndex, 1);
      // call recalculate totals
      this.calculateLineItemsTotal();
    }

    this.focusedLineIndex = -1;
  }

  // FC-4611: Fixed notifications not displaying correctly
  /**
   * save the current changes to the recurring invoice
   *
   * @returns {void}
   */
  save(objForm: NgForm): void {
    let hasEmptyAccountAndTaxCode = false;
    let hasEmptyDepartment = false;

    if (!objForm.valid) {
      // FC-4611: If objform is not valid, manually marks all form controls as touched and dirty
      Object.values(objForm.controls).forEach(control => {
        control.markAsTouched();
        control.markAsDirty();
      })

      this.getNotificationService().notifyNotAllowed('required_notice');
      return;
    }

    // no line items - show warning notification
    if (!Array.isArray(this.arLineItems) || this.arLineItems.length === 0) {
      this.getNotificationService().notifyWarning('invoice_line_items_required');
      return;
    }

    // loop each line item
    this.arLineItems.forEach(element => {
      if (isEmpty(element['objAccountCode']) || isEmpty(element['objTaxCode'])) {
        hasEmptyAccountAndTaxCode = true;
      }

      if (this.bDepartmentTracking && isEmpty(element['objDepartment'])) {
        hasEmptyDepartment = true;
      }
    });

    // emailing but no contact
    if (objForm.value.emailToClient && isEmpty(objForm.value.objContact)) {
      this.getNotificationService().notifyNotAllowed('required_notice');
      return;
    }

    // missing deptartment
    if (hasEmptyDepartment) {
      this.getNotificationService().notifyNotAllowed('required_notice');
      return;
    }

    // missing tax or account code
    if (hasEmptyAccountAndTaxCode) {
      this.getNotificationService().notifyNotAllowed('error_empty_account_and_tax_code');
      return;
    }

    this.sendToAPI();
  }



  /**
   * clears the value of the expiration date
   *
   * @param {NgForm} objForm
   */
  clearExpirationDate(objForm: NgForm): void {
    objForm.form.patchValue({ objExpiryDate: null });
    objForm.form.markAsDirty();
  }

  /**
   * Handles event when item has been selected
   *
   * @param {ILineItem} objLineItem
   *
   * @returns {void}
   */
  onItemChange(objLineItem: ILineItem): void {
    let objSelected: IItemOption = objLineItem.objItem;

    if (isEmpty(objSelected)) return;

    objLineItem.unitPrice = this.getPricebookPrice(objSelected);

    // if we have a tax code
    if (! isEmpty(objSelected.tax_code_id)) {
      objLineItem.objTaxCode = {
        id: objSelected.tax_code_id,
        text: objSelected.tax_code_name,
        code: objSelected.tax_code,
        rate: objSelected.tax_rate,
        name: get(objSelected, 'tax_code_name', get(objSelected, 'name'))
      };
    } else {
      objLineItem.objTaxCode = this.objDefaultTaxCode;
    }

    // if we have account code
    if (! isEmpty(objSelected.account_code_id)) {
      objLineItem.objAccountCode = {
        id: objSelected.account_code_id,
        text: objSelected.account_code_name,
        code: objSelected.account_code
      };
    } else {
      objLineItem.objAccountCode = this.objDefaultAccountCode;
    }

    // if we have a department
    if (! isEmpty(objSelected.department_id)) {
      objLineItem.objDepartment = {
        id: objSelected.department_id,
        text: objSelected.department_name,
      };
    } else {
      objLineItem.objDepartment = this.objDefaultDepartment;
    }

    objLineItem.description = objSelected.description || null;

    this.calculateAmount(objLineItem);
  }

  /**
   * Handles event when user open selections for items
   *
   * @param {objLineItem} objLineItem
   *
   * @returns {void}
   */
  onItemSelectOpen(objLineItem: ILineItem): void {
    objLineItem._bIsLoadingItems = true;

    this.recordService.getProductRecordData(undefined, undefined, this.getCurrentCustomerId())
      .pipe(
        finalize(() => objLineItem._bIsLoadingItems = false)
      )
      .subscribe((arItems: IItemOption[]) => {
        objLineItem._arSelectableItems$ = concat(
          of(arItems),
          objLineItem._arSelectableItems$
        );
      })
  }

  /**
   * send the data to the API
   *
   * @returns {void}
   */
  protected sendToAPI(): void {
    // generate a data payload
    let objPayloadData = this.generatePayloadData();

    this.isSubmitting = true;

    // send to the api
    this.getRecordService().saveRecord('recurring_invoices', objPayloadData, this.getData('recordID')).pipe(
      take(1),
      tap((objResponse) => {
        if (objResponse.status === StatusCode.kResponseAccepted) {
          this.notificationService.promptError(objResponse.body.error);
        }
      }),
      finalize(() => this.isSubmitting = false),
      filter((objResponse) => objResponse.status === 200 || objResponse.status === 201)
    ).subscribe((objResponse) => {
      // if have successfully created the data and close the dialog
      // if creation of new date was made shows a success add message
      // rather if it is just an update show update message
      let strMessage = (objResponse.status === 201) ? 'header_notification.success_added' : 'header_notification.success_update';

      // show notification
      this.getNotificationService().notifySuccess(strMessage)

      this.currentDialog.close({message: 'save', response: objResponse.body});
    })
  }

  /**
   * generate the payload for the main data including the line items
   * before sending to the api
   *
   * @returns {object}
   */
  protected generatePayloadData(): object {
    return {
      user_id: this.objRecurringInvoice.objAssignedUser.id,
      customer_id: this.objRecurringInvoice.objCustomer.id,
      contact_id: (this.objRecurringInvoice.objContact !== null) ? this.objRecurringInvoice.objContact.id : null,
      site_id: (this.objRecurringInvoice.objSite !== null) ? this.objRecurringInvoice.objSite.id : null,
      // period: this.objRecurringInvoice.objPeriod.id,
      invoice_summary: this.objRecurringInvoice.strInvoiceSummary,
      invoice_date: this.objRecurringInvoice.objInvoiceDate.id,
      po_number: this.objRecurringInvoice.strPurchaseOrderNumber,
      is_automatic: this.objRecurringInvoice.isAutomatic,
      is_active: this.objRecurringInvoice.isActive,
      email_to_client: this.objRecurringInvoice.emailToClient,
      next_invoice_date: this.objRecurringInvoice.objNextInvoiceDate.format('YYYY-MM-DD'),
      expiry_date: (this.objRecurringInvoice.objExpiryDate !== null) ? this.objRecurringInvoice.objExpiryDate.format('YYYY-MM-DD') : null,
      line_items: this.generateLineItemsPayload(),
      reference: this.objRecurringInvoice.strReference,
      tax_adjustment_amount: this.totalTaxAdjustment,
      amount_tax: this.fltTotalTaxAmount,
      amount_tax_ex: this.fltTotalExcludedTaxAmount,
      amount_tax_inc: this.fltTotalIncludedTaxAmount,
      payment_terms_id: (this.objRecurringInvoice.objPaymentTerm !== null) ? this.objRecurringInvoice.objPaymentTerm.id : null,
      repeated_every: this.strRepeatedEvery ? this.strRepeatedEvery : null,
      contract_id: get(this.objRecurringInvoice, 'objContract.id', null)
    }
  }

  /**
   * Build AI context specifically for recurring invoices
   */
  protected buildRecurringInvoiceAIContext(): any {
    return {
      // Core identifier
      id: this.objRecurringInvoice.strId,

      // Core parties
      customer: this.objRecurringInvoice.objCustomer,
      customer_name: (this.objRecurringInvoice.objCustomer && (this.objRecurringInvoice.objCustomer as any).text) ? (this.objRecurringInvoice.objCustomer as any).text : '',
      contact: this.objRecurringInvoice.objContact,
      contact_first_name: (this.objRecurringInvoice.objContact && this.objRecurringInvoice.objContact.text) ? this.objRecurringInvoice.objContact.text.split(' ')[0] : '',
      contact_last_name: (this.objRecurringInvoice.objContact && this.objRecurringInvoice.objContact.text) ? this.objRecurringInvoice.objContact.text.split(' ').slice(1).join(' ') : '',
      site: this.objRecurringInvoice.objSite,
      site_name: (this.objRecurringInvoice.objSite && (this.objRecurringInvoice.objSite as any).text) ? (this.objRecurringInvoice.objSite as any).text : '',
      site_site_number: (this.objRecurringInvoice.objSite && (this.objRecurringInvoice.objSite as any).text) ? (this.objRecurringInvoice.objSite as any).text : '',
      contract: this.objRecurringInvoice.objContract,

      // Dates and schedule
      invoice_date: this.objRecurringInvoice.objInvoiceDate,
      next_invoice_date: (this.objRecurringInvoice.objNextInvoiceDate &&
        (this.objRecurringInvoice.objNextInvoiceDate as any).format)
        ? (this.objRecurringInvoice.objNextInvoiceDate as any).format('YYYY-MM-DD')
        : null,
      expiry_date: (this.objRecurringInvoice.objExpiryDate && (this.objRecurringInvoice.objExpiryDate as any).format)
        ? (this.objRecurringInvoice.objExpiryDate as any).format('YYYY-MM-DD')
        : null,
      repeated_every: this.strRepeatedEvery,

      // Commercials and flags
      payment_term: this.objRecurringInvoice.objPaymentTerm,
      due_date: (this.objRecurringInvoice.objPaymentTerm && this.objRecurringInvoice.objPaymentTerm.text) ? this.objRecurringInvoice.objPaymentTerm.text : '',
      po_number: this.objRecurringInvoice.strPurchaseOrderNumber,
      reference: this.objRecurringInvoice.strReference,
      email_to_client: this.objRecurringInvoice.emailToClient,
      is_automatic: this.objRecurringInvoice.isAutomatic,
      is_active: this.objRecurringInvoice.isActive,
      assigned_user: this.objRecurringInvoice.objAssignedUser,

      // Totals snapshot for AI
      totals: {
        total_ex_tax: this.fltTotalExcludedTaxAmount,
        tax_amount: this.fltTotalTaxAmount,
        total_inc_tax: this.fltTotalIncludedTaxAmount,
        total_tax_adjustment: this.totalTaxAdjustment,
      },

      // Line items payload - format for AI
      line_items: (this.arLineItems && Array.isArray(this.arLineItems) && this.arLineItems.length > 0)
        ? this.arLineItems.map(item => ({
        description: item.description || 'No description',
        quantity: item.quantity || 1,
        unit_price: item.unitPrice || 0,
        total: item.amount || 0,
        tax_code: (item.objTaxCode && item.objTaxCode.code) || 'N/A',
        account_code: (item.objAccountCode && item.objAccountCode.code) || 'N/A',
        tax_rate: (item.objTaxCode && item.objTaxCode.rate) || 0,
        item_name: (item.objItem && item.objItem.text) || 'Unnamed Item'
          }))
        : [],
    };
  }

  /**
   * Trigger AI to generate invoice summary for recurring invoices
   */
  onGenerateRecurringInvoiceSummary(): void {
    const context = this.buildRecurringInvoiceAIContext();
    this.whenInProgress$.next(true);

    this.aiInvoiceService.generateRecurringInvoiceSummary(context).pipe(first()).subscribe({
      next: (resp: any) => {
        if (resp && resp.success) {
          let summaryContent = resp.content || '';
          this.objRecurringInvoice.strInvoiceSummary = summaryContent;
          this.notificationService.notifySuccess('ai_invoice_summary_success');
        } else {
          this.notificationService.notifyError('ai_invoice_error');
        }
      },
      error: () => this.notificationService.notifyError('ai_invoice_error'),
      complete: () => this.whenInProgress$.next(false)
    });
  }

  /**
   * Open AI prompt selector and load prompts for recurring customer invoices
   */
  openPromptSelector(): void {
    this.loadAIPrompts();
    this.showPromptSelector = true;
  }

  /** Close AI prompt selector */
  closePromptSelector(): void {
    this.showPromptSelector = false;
    this.objSelectedPrompt = null;
  }

  /** Load prompts for recurring/customer invoices */
  loadAIPrompts(): void {
    this.gptPromptService.getPrompts('recurring_customer_invoices').subscribe({
      next: (prompts) => {
        // Only show prompts intended for Recurring Customer Invoices
        const accepted = ['recurring_customer_invoices'];
        this.arAvailablePrompts = (prompts || []).filter(p => p.bIsActive && accepted.includes((p.strType || '').toLowerCase()));
      },
      error: () => {
        this.arAvailablePrompts = [];
      }
    });
  }

  /** When a prompt is selected, generate using that template */
  onSelectPrompt(prompt: GptPrompt): void {
    this.objSelectedPrompt = prompt;
    this.showPromptSelector = false;

    const context = this.buildRecurringInvoiceAIContext();
    this.whenInProgress$.next(true);

    // Resolve {{ variables }} in the selected prompt using Liquid.js and the built context
    let processedPrompt = (prompt && prompt.strPromptTemplate) ? prompt.strPromptTemplate : '';
    const liquidPromise = this.liquidEngine
      .parseAndRender(processedPrompt, context)
      .then(resolved => {
        processedPrompt = resolved;

        // If line items exist and weren't already included via {{ line_items } CONS>, append them
        if (context.line_items && context.line_items.length > 0 && !resolved.includes('{{ line_items }}')) {
          const lineItemsText = context.line_items.map((item, index) => {
            const name = item.item_name || '';
            const desc = item.description || '';
            const label = name && desc && name !== desc ? `${name} - ${desc}` : (name || desc || '');
            return `${index + 1}. ${label}`.trim();
          }).join('\n');

          processedPrompt += `\n\nLine Items to include in summary:\n${lineItemsText}`;
        }
      })
      .catch(() => {
        // If Liquid parsing fails, show error to user and continue with original template
        this.notificationService.notifyError('liquid_template_error');
      });

    Promise.resolve(liquidPromise).finally(() => {
      this.aiInvoiceService.generateRecurringInvoiceSummary(context, processedPrompt).pipe(first()).subscribe({
        next: (resp: any) => {
          if (resp && resp.success) {
            this.objRecurringInvoice.strInvoiceSummary = resp.content || '';
            this.notificationService.notifySuccess('ai_invoice_summary_success');
          } else {
            this.notificationService.notifyError('ai_invoice_error');
          }
        },
        error: () => this.notificationService.notifyError('ai_invoice_error'),
        complete: () => this.whenInProgress$.next(false)
      });
    });
  }

  /**
   * generates the current payload for line items before sending it
   * to the api and formats it
   *
   * @returns Array<any>
   */
  protected generateLineItemsPayload(): Array<any> {
    // loop each line items data and push to the payload
    return this.arLineItems.map((objLineItem: ILineItem) => {
      const objItem: IItemOption = objLineItem.objItem;

      return {
        item_id: get(objItem, 'id', null),
        item_code: get(objItem, 'code', null),
        item_name: get(objItem, 'text', null),
        labor: get(objItem, 'labor'),
        quantity: objLineItem.quantity,
        unit_price: objLineItem.unitPrice,
        description: objLineItem.description,
        tax_code_id: (objLineItem.objTaxCode !== null) ? objLineItem.objTaxCode.id : null,
        total_price: objLineItem.amount,
        account_code_id: (objLineItem.objAccountCode !== null) ? objLineItem.objAccountCode.id : null,
        department_id: this.bDepartmentTracking ? objLineItem.objDepartment.id : null,
        account_code: (objLineItem.objAccountCode !== null) ? objLineItem.objAccountCode.code : null,
        tax_code: (objLineItem.objTaxCode !== null) ? objLineItem.objTaxCode.code : null,
        asset_type_id: get(objLineItem, 'asset_type_id', null),
      };
    });
  }

  /**
   * retrieves our record service for fetching api data
   *
   * @returns {RecordService}
   */
  protected getRecordService(): RecordService {
    return this.recordService;
  }

  /**
   * retrieves the injected translation service
   *
   * @returns {CustomTranslateService}
   */
  protected getTranslationService(): CustomTranslateService {
    return this.translationService;
  }

  /**
   * retrieves our notification service instance
   *
   * @returns {NotificationService}
   */
  protected getNotificationService(): NotificationService {
    return this.notificationService;
  }

  /**
   * retrieves view service instance
   *
   * @returns {ViewService}
   */
  protected getViewService(): ViewService {
    return this.viewService;
  }

  /**
   * get the current data value or the current data object it self
   *
   * @param {string} strKey
   */
  protected getData(strKey: string): any {
    return (strKey === '*') ? this.objData : this.objData[strKey];
  }

  /**
   * retrieves the current record id of the recurring invoice
   *
   * @returns {string}
   */
  getCurrentRecordID(): string {
    return this.strCurrentRecordID;
  }

  /**
   * This will get the current id of the customer
   */
  protected getCurrentCustomerId(): string|null {
    return (this.getCurrentCustomer() && this.getCurrentCustomer().id) ? this.getCurrentCustomer().id : null;
  }

  /**
   * get the current customer object from the recurring invoice
   * data
   *
   * @return {ICustomerOption}
   */
  protected getCurrentCustomer(): ICustomerOption {
    return this.objRecurringInvoice.objCustomer;
  }

  /**
   * get the current site object from the recurring invoice data
   *
   * @return {ISiteOption}
   */
  protected getCurrentSite(): ISiteOption {
    return this.objRecurringInvoice.objSite;
  }

  /**
   * This will get the current id of the site
   */
  protected getCurrentSiteId(): string|null {
    return (this.getCurrentSite() && this.getCurrentSite().id) ? this.getCurrentSite().id : null;
  }

  /**
   * get the current site object from the recurring invoice data
   *
   * @return {IPaymentTermOption}
   */
  protected getCurrentPaymentTerm(): IPaymentTermOption {
    return this.objRecurringInvoice.objPaymentTerm;
  }

   /**
   * This will get the current id of the site
   */
   protected getCurrentPaymentTermId(): string|null {
    return (this.getCurrentPaymentTerm() && this.getCurrentPaymentTerm().id) ? this.getCurrentPaymentTerm().id : null;
  }

  /**
   * sets the current record of the recurring invoice
   *
   * @param   {string|null} strRecordID
   *
   * @returns {void}
   */
  setCurrentRecordID(strRecordID?: string): void {
    if (! this.strCurrentRecordID) {
      this.strCurrentRecordID = strRecordID;
    }
  }

  onIntervalChange() {
    if (this.strQuantity && this.strFrequency) {
      this.strRepeatedEvery = `${this.strQuantity} ${this.strFrequency}`
    }
  }

  /**
   * sets the current customer ID propery value
   *
   * @param {string|ICustomerOption} objCustomer
   */
  protected setCurrentCustomer(objCustomer: string|ICustomerOption): void {
    this.objRecurringInvoice.objCustomer = (typeof objCustomer === 'object')
      ? objCustomer
      : { id: objCustomer, text: null };
  }

  /**
   * sets the current value of property site ID
   *
   * @param {string|null|ISiteOption} objSite
   */
  protected setCurrentSite(objSite: string|null|ISiteOption): void {
    if (typeof objSite === 'object') {
      this.objRecurringInvoice.objSite = objSite;
      if (this.isCreateForm() && ! isEmpty(objSite)) {
        this.objRecurringInvoice.strReference = objSite.text.replace(/ *\[[^)]*\] */g, "");
      }
    } else {
      this.objRecurringInvoice.objSite = { id: objSite, text: null };
    }
  }

  /**
   * sets the current value of payment terms
   *
   * @param {string|null|IPaymentTermOption} objPaymentTerm
   */
  protected setCurrentPaymentTerm(objPaymentTerm: string|null|IPaymentTermOption): void {
    this.objRecurringInvoice.objPaymentTerm = (typeof objPaymentTerm === 'object')
      ? objPaymentTerm
      : { id: objPaymentTerm, text: null };
  }

  /**
   * sets the current selectable customers list
   *
   * @param {Array<ICustomerOption>} arCustomers
   */
  protected setSelectableCustomers(arCustomers: Array<ICustomerOption>): void {
    // check if we have a selected customer
    let objCurrentCustomer = this.getCurrentCustomer();

    if (objCurrentCustomer !== null) {
      // look for the array of customers if one is matching set at as the found
      // rather get the first one instead
      this.setCurrentCustomer(arCustomers.find((objCustomer) => ( objCustomer.id === objCurrentCustomer.id )) || arCustomers[0]);
    }

    this.arSelectableRelatedCustomers$ = concat(
      of(arCustomers),
      this.objSearchCustomerInput$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.isLoadingRelated.customers = true),
        switchMap((strTerm: string) => {
          return this.recordService.getRecordRelate('customers', strTerm, '', false, false)
            .pipe(
              tap(() => this.isLoadingRelated.customers = false)
            );
        })
      )
    );
  }

  /**
   * sets the current selectable sites list
   *
   * @param {Array<ISiteOption>} arSites
   */
  protected setSelectableSites(arSites: Array<ISiteOption>): void {
    // check if we have a selected site
    let objCurrentSite = this.getCurrentSite();

    if (objCurrentSite !== null) {
      // look for the array of customers if one is matching set at as the found
      this.setCurrentSite(arSites.find((objSite) => ( objSite.id === objCurrentSite.id )));
    }

    this.arSelectableRelatedSites$ = of(arSites);
  }

   /**
   * sets the current selectable sites list
   *
   * @param {Array<IPaymentTermOption>} arPaymentTerms
   */
   protected setSelectablePaymentTerms(arPaymentTerms: Array<IPaymentTermOption>): void {
    // check if we have a selected site
    let objCurrentPaymentTerm = this.getCurrentPaymentTerm();

    if (filled(objCurrentPaymentTerm)) {
      // look for the array of customers if one is matching set at as the found
      this.setCurrentPaymentTerm(arPaymentTerms.find((objPaymentTerm) => ( objPaymentTerm.id === objCurrentPaymentTerm.id )));
    }

    this.arSelectableRelatedPaymentTerms$ = of(arPaymentTerms);
  }

  /**
   * sets the current selectable contacts list
   *
   * @param {Array<IContactOption>} arContacts
   */
  protected setSelectableContacts(objResponse: any): void {
    let arContacts: Array<IContactOption> = [];

    // related contacts(customers)
    let arCustomersContact = objResponse['contact_customers'] || [];
    if (arCustomersContact.length > 0) {
      arCustomersContact.forEach((objContact) => {
        arContacts.push({
          id: objContact['contact_id'],
          text: objContact['contact_text'] || objContact['text'],
          groupname: 'contact_customers',
          role: this._computeDisplayedRole(objContact['role']),
        });
      });
    }

    // related contacts(sites)
    let arSitesContact = objResponse['contact_sites'] || [];
    if (arSitesContact.length > 0) {
      arSitesContact.forEach((objContact) => {
        arContacts.push({
          id: objContact['contact_id'],
          text: objContact['contact_text'] || objContact['text'],
          groupname: 'contact_sites',
          role: this._computeDisplayedRole(objContact['role']),
        });
      });
    }

    // if contact are both empty from related contacts lets merged the general contacts
    let arDefaultContacts = objResponse['contacts'] || [];

    if (arContacts.length == 0) {
      arDefaultContacts.forEach((objContact) => {
        arContacts.push({
          id: objContact['id'],
          text: objContact['customer_text'] || objContact['text'],
        });
      });
    }

    // check if we have a current contact from the current recurring invoice data
    let objCurrentContact = this.objRecurringInvoice.objContact;

    if (objCurrentContact !== null) {
      // we override the current object contact we the data from the
      // contacts list if none found then we revert it back to as it was before or rather null?
      objCurrentContact = arContacts.find((objContact) => (objContact.id === objCurrentContact.id)) || null;

      // backward compatibility for selected that was previously selected from the general contacts
      // if the current selected contact was not found in the contacts lets try finding it in the default contacts
      if (objCurrentContact == null) {
        let objPreviouslySelected = this.objRecurringInvoice.objContact;
        objCurrentContact = arDefaultContacts.find((objContact) => (objContact.id === objPreviouslySelected.id)) || null;
      }

      this.objRecurringInvoice.objContact = objCurrentContact;
    }

    this.arSelectableRelatedContacts$ = concat(
      of(arContacts),
      this.objRelatedContactInput$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.isLoadingRelatedContacts = true),
        switchMap((strTerm: string) => {
          return this.recordService.getRecordRelate('contacts', strTerm, '', false, false)
            .pipe(tap(() => this.isLoadingRelatedContacts = false));
        })
      )
    )
  }

  /**
   * sets the current list of selectable departments
   *
   * @param {any} arDepartments
   */
  protected setSelectableDepartments(arDepartments: Array<IDepartmentOption>): void {
    // check if we have pre selected department for each line items
    this.arLineItems.map((objLineItem: ILineItem) => {
      if (objLineItem.objDepartment) {
        objLineItem.objDepartment = arDepartments.find((objDepartment) => (objDepartment.id === objLineItem.objDepartment.id)) || objLineItem.objDepartment;
      }

      return objLineItem;
    });

    this.arCachedDepartments = arDepartments;
  }

  /**
   * sets the current list of selectable tax codes
   *
   * @param {any} arTaxCodes
   */
  protected setSelectableTaxCodes(arTaxCodes: Array<ITaxCodeOption>): void {
    // check if we have pre selected tax code for each line items
    this.arLineItems.map((objLineItem: ILineItem) => {
      if (objLineItem.objTaxCode) {
        objLineItem.objTaxCode = arTaxCodes.find((objTaxCode) => (objTaxCode.id === objLineItem.objTaxCode.id)) || objLineItem.objTaxCode;
      }

      return objLineItem;
    });

    this.arCachedTaxCodes = arTaxCodes;
  }

  /**
   * sets the current list of selectable account codes
   *
   * @param {any} arAccountCodes
   */
  protected setSelectableAccountCodes(arAccountCodes: Array<IAccountCodeOption>): void {
    // check if we have pre selected department for each line items
    this.arLineItems.map((objLineItem: ILineItem) => {
      if (objLineItem.objAccountCode) {
        objLineItem.objAccountCode = arAccountCodes.find((objAccountCode) => (objAccountCode.id === objLineItem.objAccountCode.id)) || objLineItem.objAccountCode;
      }

      return objLineItem;
    });

    this.arCachedAccountCodes = arAccountCodes;
  }

  /**
   * sets the current selectable assigned users list
   *
   * @param {IAssignedUserOption[]} arAssignedUsers
   */
  protected setSelectableAssignedUsers(arAssignedUsers: IAssignedUserOption[]): void {
    let objCurrentAssignedUser = this.objRecurringInvoice.objAssignedUser;
    let objCurrentSelectedAssignedUser = null;

    // if we have already selected assigned user
    if (objCurrentAssignedUser) {
      objCurrentSelectedAssignedUser = arAssignedUsers.find(objAssignedUser => objCurrentAssignedUser.id === objAssignedUser.id) || null;
    }

    // if we still don't have the selected user we should set the default to the current authenticated user
    // is this the right way of having the current authenticated user id
    if (! objCurrentSelectedAssignedUser) {
      let strCurrentAuthenticatedUserId = this.localStorage.getItem('user_id');
      objCurrentSelectedAssignedUser = arAssignedUsers.find(objAssignedUser => objAssignedUser.id === strCurrentAuthenticatedUserId) || null;

      // if we still don't have the current authenticated user lets push it to the options rather
      if (! objCurrentSelectedAssignedUser) {
        let strCurrentAuthenticatedUserName = this.localStorage.getItem('user_name');
        objCurrentSelectedAssignedUser = { id: strCurrentAuthenticatedUserId, text: strCurrentAuthenticatedUserName};
        arAssignedUsers.push(objCurrentSelectedAssignedUser);
      }
    }

    // overrides the current value with the proper value from the list
    this.objRecurringInvoice.objAssignedUser = objCurrentSelectedAssignedUser;

    this.arSelectableAssignedUser$ = concat(
      of(arAssignedUsers),
      this.objRelatedAssignedUserInput$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.isLoadingRelated['users'] = true),
        switchMap((strTerm: string) => {
          return this.recordService.getRecordRelate('users', strTerm, '', false, false)
            .pipe(tap(() => this.isLoadingRelated['users'] = false));
        })
      )
    );
  }

  /**
   * sets the current selected period if any from the recurring data
   *
   * @param   {IPeriodOption|null} objCurrentPeriod
   *
   * @returns {void}
   */
  protected setSelectedPeriod(objCurrentPeriod?: IPeriodOption): void {
    // if set then look from the list
    if (objCurrentPeriod) {
      this.arSelectablePeriods$.subscribe((arPeriods) => {
        this.objRecurringInvoice.objPeriod = arPeriods.find((objPeriod) => (objPeriod.id === objCurrentPeriod.id)) || null;
      });
    }
  }

  /**
   * sets the current selected invoice date of the recurring invoice data
   *
   * @param   {IDatePeriodOption|null} objCurrentInvoiceDate
   *
   * @returns {void}
   */
  protected setSelectedInvoiceDate(objCurrentInvoiceDate?: IDatePeriodOption): void {
    if (objCurrentInvoiceDate) {
      this.arSelectableDatePeriods$.subscribe((arDatePeriods) => {
        this.objRecurringInvoice.objInvoiceDate = arDatePeriods.find((objDatePeriod) => (objDatePeriod.id === objCurrentInvoiceDate.id)) || null;
      });
    }
  }

  /**
   * initializes all the selectable fields values
   *
   * @returns {void}
   */
  protected initSelectableFieldsValues(): void {

    // sets the current selected period
    this.setSelectedPeriod(this.objRecurringInvoice.objPeriod);
    this.setSelectedInvoiceDate(this.objRecurringInvoice.objInvoiceDate);
    this.initLineItems();

    this.initContractRelate();
  }

  /**
   * sets the current customer ID propery value
   *
   * @param {IContractOption} contract
   */
  protected setCurrentContract(contract: IContractOption): void {
    this.objRecurringInvoice.objContract = contract;
  }

  /**
   * initializes related modules select field values
   *
   * @param {string} strModule
   * @returns {void}
   */
  public initRelatedSelectableFieldValues(strModule: string): void
  {
    if (strModule === 'contacts') {
      let objFiltersPayload = {};
      this.isLoadingRelatedContacts = true;

      if (this.getData('moduleName') === 'customers') {
        objFiltersPayload = Object.assign(objFiltersPayload, {
          customer_id: this.getCurrentCustomerId(),
        });
      } else if (this.getData('moduleName') === 'sites') {
        objFiltersPayload = Object.assign(objFiltersPayload, {
          site_id: this.getCurrentSiteId(),
          customer_id: this.getCurrentCustomerId(),
        });
      } else if (this.getData('moduleName') === 'payment_terms') {
        objFiltersPayload = Object.assign(objFiltersPayload, {
          site_id: this.getCurrentSiteId(),
          customer_id: this.getCurrentCustomerId(),
          payment_terms_id: this.getCurrentPaymentTermId(),
        });
      }

      this.getRecordService().getMultipleModuleRelateRecord('contact_roles', false, objFiltersPayload)
      .subscribe((objResponse) => {
        // related contacts
        this.setSelectableContacts(objResponse);
        this.isLoadingRelatedContacts = false;
      });
    } else if (strModule == 'sites') {
      this.getRelateRecord('sites').subscribe((objResponse) => {
        this.siteRelateRecord(objResponse);
      });
    } else if (strModule == 'payment_terms') {
      this.getRelateRecord('payment_terms').subscribe((objResponse) => {
        this.paymentTermsRelateRecord(objResponse);
      });
    }
    else {
      this.isLoadingRelated[strModule] = true;

      this.recordService.getRecordRelate(strModule, '', [], false)
      .subscribe((objResponse) => {
        switch(strModule) {
          case 'customers':
            // related customers
            let arCustomers = objResponse as ICustomerOption[];
            this.setSelectableCustomers(arCustomers);
            this.isLoadingRelated[strModule] = false;
          break;
          case 'sites':
            // related sites
            let arSites = objResponse as ISiteOption[];
            this.setSelectableSites(arSites);
            this.isLoadingRelated[strModule] = false;
          break;
          case 'users':
            // assigned users
            let arAssignedUsers = objResponse as IAssignedUserOption[];
            this.setSelectableAssignedUsers(arAssignedUsers);
            this.isLoadingRelated[strModule] = false;
          break;
          case 'payment_terms':
            let arPaymentTerms = objResponse as IPaymentTermOption[];
            this.setSelectablePaymentTerms(arPaymentTerms);
            this.isLoadingRelated[strModule] = false;
        }
      });
    }
  }

  /**
   * Updates the value of the field to Observable select
   *
   * @param arRelateInitialValue
   * @param arFilter
   *
   * @returns void
   */
  public siteRelateRecord(arRelateInitialValue = [], arFilter = []): void {

    this.arSelectableRelatedSites$ = concat(
      of(arRelateInitialValue), // We set the initial values to the ng-select.
      this.arSelectableRelatedSitesInput$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.isLoadingRelated['sites'] = true),
        switchMap(term => this.getRelateRecord('sites', term, {}).pipe(
          tap((data) => {
            this.isLoadingRelated['sites'] = false;
          }),
        ))
      ),
    );
  }

  /**
   * Updates the value of the field to Observable select
   *
   * @param arRelateInitialValue
   *
   * @returns void
   */
  public paymentTermsRelateRecord(arRelateInitialValue: Select[] = [], arFilter: any[] = []): void {
    this.arSelectableRelatedPaymentTerms$ = new Relate<any>().buildRelates(
      _ => concat(
        of(arRelateInitialValue), // Set the initial values to the ng-select.
        this.arSelectableRelatedPaymentTermsInput$.pipe(
          debounceTime(400),
          distinctUntilChanged(),
          tap(() => this.isLoadingRelated['payment_terms'] = true),
          switchMap(term => this.getRelateRecord('payment_terms', term, { ...arFilter, is_customer: true }).pipe(
            tap(() => {
              this.isLoadingRelated['payment_terms'] = false;
            })
          ))
        )
      ),
      arRelateInitialValue,
      !isEmpty(arRelateInitialValue)
    ).source;
  }

  /**
   * Here we can manipulate the relate filter before requesting it to api
   *
   * @param relateModule
   * @param terms
   * @param filter
   *
   * @retuns Observable<Select[]>
   */
  public getRelateRecord(relateModule, terms = null, filter = {}): Observable<Select[]> {

    filter = (filter) ? filter : {};
    let strCustomerId = (this.objRecurringInvoice.objCustomer) ? this.objRecurringInvoice.objCustomer['id'] : null;
    if (relateModule === 'sites' && (terms === null || terms === '' ) && strCustomerId != null) {
      filter['customer_id'] = strCustomerId;
    }

    if (relateModule === 'payment_terms') {
      filter['is_customer'] = true;
    }
    return this.recordService.getRecordRelate(relateModule, terms, false, false, filter);
  }

  /**
   * initialize the line items
   *
   * @returns {void}
   */
  protected initLineItems(): void {
    if (this.isCreateForm()) {
      // append empty line item if we only have a create form
      this.addEmptyLineItem();
    } else {
      this.arLineItems.map((objLineItem) => {
        // calculate each items amount
        this.calculateAmount(objLineItem);

        return objLineItem;
      });
    }

    // calulate totals
    this.calculateLineItemsTotal();

    setTimeout(() => {
      this.isLineItemsLoaded = true;
    }, 500);
  }

  /**
   * initializes data for the editing of recurring invoice data
   *
   * @returns {void}
   */
  protected initEditRecurringInvoiceForm(): void {
    this.getRecordService().getRecord(
      'recurring_invoices', this.getCurrentRecordID(), true, {}, 'edit_form'
    ).subscribe((objResponse) => {
      this.initRecurringInvoiceForm(objResponse);
    });
  }

  /**
   * initializes data to recurring invoice form
   */
  protected initRecurringInvoiceForm(data: LooseObject): void {
    // it seems it returns a HttpResponse object when failure occurs
    // so we would not continue to process the response since it determines failure on request
    // might contain 404 Not found status
    if (data.status) {
      return;
    }

    let objRecord = data['record_details'];

    // create the recurring invoice data by merging with default
    this.objRecurringInvoice = Object.assign(this.objRecurringInvoice, {
      strId: objRecord['id'],
      isActive: objRecord['is_active'],
      isAutomatic: objRecord['is_automatic'],
      emailToClient: objRecord['email_to_client'] || false,
      objPeriod: { id: objRecord['period'] },
      objNextInvoiceDate: this.createMomentFromRawDateString(objRecord['next_invoice_date']),
      objCustomer: { id: objRecord['customer_id'] },
      objContact: (objRecord['contact_id']) ? { id: objRecord['contact_id'] } : null,
      objSite: (objRecord['site_id']) ? { id: objRecord['site_id'] } : null,
      objExpiryDate: (objRecord['expiry_date']) ? this.createMomentFromRawDateString(objRecord['expiry_date']) : null,
      strPurchaseOrderNumber: objRecord['po_number'],
      objInvoiceDate: { id: objRecord['invoice_date'] },
      strInvoiceSummary: objRecord['invoice_summary'],
      strReference: objRecord['reference'],
      objAssignedUser: {
        id: objRecord['user_id'],
        text: objRecord['user_text'],
      },
      objPaymentTerm: { id: objRecord['payment_terms_id'] },
      objContract: { id: objRecord['contract_id'], text: objRecord['contract_text'] },
    });

    this.arLineItems = objRecord['line_items'].map((objLineItem) => {
      let _objLineItem = {
        objItem: (! isEmpty(objLineItem['item_id'])) ? { id: objLineItem['item_id'], text: objLineItem['item_name'], code: objLineItem['item_code'], labor: objLineItem['labor'] } : null,
        quantity: objLineItem['quantity'],
        description: objLineItem['description'],
        objDepartment: {
          id: objLineItem['department_id'],
          text: objLineItem['department_name'],
        },
        objTaxCode: (! isEmpty(objLineItem['tax_code_id'])) ? {
          id: objLineItem['tax_code_id'],
          text: objLineItem['tax_code_name'],
          code: objLineItem['tax_code'],
          rate: objLineItem['tax_rate'],
          name: get(objLineItem, 'tax_code_name', get(objLineItem, 'name')),
        } : null,
        objAccountCode: (! isEmpty(objLineItem['account_code_id'])) ? {
          id: objLineItem['account_code_id'],
          text: objLineItem['account_code_name'],
          code: objLineItem['account_code'],
        } : null,
        unitPrice: objLineItem['unit_price'],
        amount: objLineItem['total_price'],
        select_line_id: UUID.UUID(),
        related_products: get(objLineItem, 'related_products', []),
        supplier_pricing: get(objLineItem, 'supplier_pricing', []),
        module: get(objLineItem, 'module', 'items'),
        asset_type_id: get(objLineItem, 'asset_type_id', null),
      } as ILineItem;

      _objLineItem = this.appendItemSearch(_objLineItem);

      return _objLineItem;
    });

    this.totalTaxAdjustment = toFormattedNumber(get(objRecord, 'tax_adjustment_amount'), {
      currency: true,
    });

    // departments
    let arDepartments = data['related_data']['departments'] || [];
    this.setSelectableDepartments(arDepartments);

    // tax codes
    let arTaxCodes = data['related_data']['tax_codes'] || [];
    this.setSelectableTaxCodes(arTaxCodes);

    // account codes
    let arAccountCodes = data['related_data']['account_codes'] || [];
    this.setSelectableAccountCodes(arAccountCodes);

    let objCustomer = {id: objRecord['customer_id'], text: objRecord['customer_text']};
    let objSite = {id: objRecord['site_id'], text: objRecord['site_text']};
    let objUser = {id: objRecord['user_id'], text: objRecord['user_text']};
    let objContactSelect = [{id: objRecord['contact_id'], text: objRecord['contact_text']}];
    let objContact = {contacts : objContactSelect};
    let objPaymentTerm = [{id: objRecord['payment_terms_id'], text: objRecord['payment_terms_text']}];
    // sets customer selected
    this.setCurrentCustomer(objCustomer);
    // sets site selected
    this.setCurrentSite(objSite);
    // sets user selected
    this.setSelectableAssignedUsers([objUser]);
    // sets site selected
    this.setSelectableContacts(objContact);
    // sets payment terms selected
    this.setSelectablePaymentTerms(objPaymentTerm);

    // defaults
    this.setRelatedDefaults(data['related_data']);

    if (objRecord['repeated_every']) {
      const [ initQty, initFreq ] = objRecord['repeated_every'].split(' ');
      let frequency = initFreq.replace(/s$/, '');
      let quantity = parseInt(initQty, 10);
      if (quantity % 7 == 0 && frequency == 'day') {
        frequency = 'week';
        quantity = quantity / 7;
      }
      if (frequency.includes('mon')) {
        frequency = 'month';
      }
      this.strQuantity = quantity.toString();
      this.strFrequency = frequency;
      this.onIntervalChange();
    }
    // initialize fields
    this.initSelectableFieldsValues();
  }

  /**
   * initializes edit/create form for customer module
   *
   * @returns {void}
   */
  protected initCustomerModuleRecurringInvoice(objParentViewRecord): void {
    // if we dont have a parent view record dont process
    if (! objParentViewRecord) {
      return;
    }

    // we only need the details
    let objRecordDetails = objParentViewRecord['record_details'];
    let objCustomer = {id: objRecordDetails['id'], text: objRecordDetails['text']};

    // sets current customer
    this.setCurrentCustomer(objCustomer);
    this.setSelectableAssignedUsers([]);

    // if we have a create form
    if (this.isCreateForm()) {
      // Get config of customer invoice
      this.recordService.getRecordConfig('recurring_invoices', null, true).subscribe((objConfig) => {
        // defaults
        this.setRelatedDefaults(objConfig['related_data']);

        // set values for the selected fields
        this.initSelectableFieldsValues();

        // departments
        let arDepartments = objConfig['related_data']['departments'] || [];
        this.setSelectableDepartments(arDepartments);

        // tax codes
        let arTaxCodes = objConfig['related_data']['tax_codes'] || [];
        this.setSelectableTaxCodes(arTaxCodes);

        // account codes
        let arAccountCodes = objConfig['related_data']['account_codes'] || [];
        this.setSelectableAccountCodes(arAccountCodes);

        // defaults
        this.setRelatedDefaults(objConfig['related_data']);

        // set values for the selected fields
        this.initSelectableFieldsValues();

        // set the default customer payment terms
        this.setDefaultPaymentTerms(objRecordDetails);
      });
    } else {
      this.setCurrentRecordID(this.getData('recordID'));
      this.initEditRecurringInvoiceForm();
    }
  }

  /**
   * initializes edit/create form for customer module
   *
   * @returns {void}
   */
  protected initSiteModuleRecurringInvoice(objParentViewRecord = undefined): void {
    // if we dont have a parent view record dont process
    if (! objParentViewRecord) {
      return;
    }

    // we only need the details
    let objRecordDetails = objParentViewRecord['record_details'];
    let objCustomer = {id: objRecordDetails['customer_id'], text: objRecordDetails['customer_text']};
    let objSite = {id: objRecordDetails['id'], text: objRecordDetails['text']};

    // sets current customer
    this.setCurrentCustomer(objCustomer);
    this.setCurrentSite(objSite);
    this.setSelectableAssignedUsers([]);


    // if we have a create form
    if (this.isCreateForm()) {
      // Get config of customer invoice
      this.recordService.getRecordConfig('recurring_invoices', null, true).pipe(
        first(),
      ).subscribe( objConfig => {
        // departments
        let arDepartments = objConfig['related_data']['departments'] || [];
        this.setSelectableDepartments(arDepartments);

        // tax codes
        let arTaxCodes = objConfig['related_data']['tax_codes'] || [];
        this.setSelectableTaxCodes(arTaxCodes);

        // account codes
        let arAccountCodes = objConfig['related_data']['account_codes'] || [];
        this.setSelectableAccountCodes(arAccountCodes);

        // defaults
        this.setRelatedDefaults(objConfig['related_data']);

        // set values for the selected fields
        this.initSelectableFieldsValues();

        // set the default customer payment terms
        this.setDefaultPaymentTerms(objRecordDetails);
      });
    } else {
      this.setCurrentRecordID(this.getData('recordID'));
      this.initEditRecurringInvoiceForm();
    }
  }

  /**
   * pushes an empty line item object to the line items list
   *
   * @returns {void}
   */
  public addEmptyLineItem(objData: LooseObject = null): void {
    if (objData) {
      let taxCode: ITaxCodeOption|null = this.objDefaultTaxCode;
      let accountCode: IAccountCodeOption|null = this.objDefaultAccountCode;
      let department: IDepartmentOption|null = this.objDefaultDepartment;

      if (filled(get(objData, 'account_code_id'))) {
        accountCode = {
          id: get(objData, 'account_code_id'),
          code: get(objData, 'account_code'),
          text: get(objData, 'account_code_name'),
        };
      }

      if (filled(get(objData, 'tax_code_id'))) {
        taxCode = {
          id: get(objData, 'tax_code_id'),
          code: get(objData, 'tax_code'),
          text: get(objData, 'tax_code_name'),
          rate: toFormattedNumber(get(objData, 'tax_rate')),
          name: get(objData, 'tax_code_name', get(objData, 'name'))
        };
      }

      if (filled(get(objData, 'department_id'))) {
        department = {
          id: get(objData, 'department_id'),
          text: get(objData, 'department_name'),
        };
      }

      let intUnitPrice = filled(objData['contract_unit_price']) ? objData['contract_unit_price'] : objData['unit_price'];
      let objLineItem = {
        quantity: get(objData, 'quantity', 1),
        description: objData['description'],
        objDepartment: department,
        objTaxCode: taxCode,
        unitPrice: intUnitPrice || 0,
        objAccountCode: accountCode,
        amount: intUnitPrice || 0,
        isInvalidDepartment: false,
        isInvalidTaxCode: false,
        isInvalidAccountCode: false,
        stock: objData['current_stock_level'] || 0,
        select_line_id: UUID.UUID(),
        related_products: get(objData, 'related_products'),
        supplier_pricing: get(objData, 'supplier_pricing'),
        module: get(objData, 'module', 'items'),
        asset_type_id: get(objData, 'asset_type_id', null),
        repeated_every: get(objData, 'repeated_every', null),
      } as ILineItem;

      objLineItem = this.appendItemSearch(objLineItem);
      objLineItem.objItem = {
        "id": get(objData, 'item_id', get(objData, 'id')),
        "code": get(objData, 'item_code', get(objData, 'code')),
        "text": get(objData, 'item_name', get(objData, 'name')),
        "labor": get(objData, 'labor'),
      } as any;

      if (this.focusedLineIndex > -1) {
        const arLines = clone(this.arLineItems);

        arLines.splice(this.focusedLineIndex, 0, objLineItem);

        this.arLineItems = arLines;
      } else {
        this.arLineItems.push(objLineItem);
      }
    }

    this.calculateLineItemsTotal();
  }

  onLineFocus(index: number): void {
    this.focusedLineIndex = index + 1;
  }

  /**
   * creates an instance of the Moment from a given raw date string
   * usually came from the API which removes the UTC and recreates it based
   * on a local/client utc
   *
   * @param {string} strRawDate
   */
  protected createMomentFromRawDateString(strRawDate: string): Moment {
    return moment(
      moment.utc(strRawDate).toDate()
    ).local();
  }

  /**
   * Build object for item search per line item
   *
   * @param {ILineItem} intIdx
   *
   * @returns {ILineItem}
   */
  protected appendItemSearch(objLineItem: ILineItem): ILineItem {

    objLineItem._bIsLoadingItems = false;
    objLineItem._objItemTypeahead$ = new Subject<string>();
    objLineItem._arSelectableItems$ = objLineItem._objItemTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingItems = true),
      switchMap((strTerm) => this.recordService.getProductRecordData(strTerm, undefined, this.getCurrentCustomerId()).pipe(
        tap(() => objLineItem._bIsLoadingItems = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
          code: objOption['code'],
          unit_price: objOption['unit_price'],
          pricebook_unit_price: objOption['pricebook_unit_price'],
          tax_code_id: objOption['tax_code_id'],
          tax_code_name: objOption['tax_code_name'],
          tax_code: objOption['tax_code'],
          tax_rate: objOption['tax_rate'],
          account_code_id: objOption['account_code_id'],
          account_code_name: objOption['account_code_name'],
          account_code: objOption['account_code'],
          department_id: objOption['department_id'],
          department_name: objOption['department_name'],
          description: objOption['description'],
          labor: objOption['labor'],
        })))
      ))
    );

    objLineItem._bIsLoadingDepartments = false;
    objLineItem._objDepartmentTypeahead$ = new Subject<string>();

    objLineItem._arSelectableDepartments$ = objLineItem._objDepartmentTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingDepartments = true),
      switchMap((strTerm) => this.getRelateRecord('departments', strTerm).pipe(
        tap(() => objLineItem._bIsLoadingDepartments = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
        })))
      ))
    );

    objLineItem._bIsLoadingAccountCodes = false;
    objLineItem._objAccountCodeTypeahead$ = new Subject<string>();
    objLineItem._arSelectableAccountCodes$ = objLineItem._objAccountCodeTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingAccountCodes = true),
      switchMap((strTerm) => this.getRelateRecord('account_codes', strTerm).pipe(
        tap(() => objLineItem._bIsLoadingAccountCodes = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
          code: objOption['code'],
        })))
      ))
    );

    objLineItem._bIsLoadingTaxCodes = false;
    objLineItem._objTaxCodeTypeahead$ = new Subject<string>();
    objLineItem._arSelectableTaxCodes$ = objLineItem._objTaxCodeTypeahead$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => objLineItem._bIsLoadingTaxCodes = true),
      switchMap((strTerm) => this.getRelateRecord('tax_codes', strTerm).pipe(
        tap(() => objLineItem._bIsLoadingTaxCodes = false),
        map((arOptions) => arOptions.map((objOption) => ({
          id: objOption.id,
          text: objOption.text,
          code: objOption['code'],
          rate: objOption['rate'],
          name: objOption['name'],
        })))
      ))
    );

    return objLineItem;
  }

  /**
   * Set default related modules
   *
   * @param {IRelatedModuleData} objRelated
   *
   * @returns {void}
   */
  protected setRelatedDefaults(objRelated: IRelatedModuleData): void {
    this.objDefaultAccountCode = objRelated.default_account_code_sale;
    this.objDefaultTaxCode = objRelated.default_tax_code_sale;
    this.objDefaultPaymentTerm = objRelated.default_customer_payment_term;
    this.objDefaultDepartment = objRelated.default_department;
  }

  /**
   * get the pricebook unit price in item
   *
   * @param objItem
   *
   * @returns {number}
   */
  protected getPricebookPrice(objItem: IItemOption): number {
    if (this.getCurrentCustomerId() && objItem.pricebook_unit_price) {
      return objItem.pricebook_unit_price
    } else {
      return objItem.unit_price;
    }
  }

  /**
   * Handles open selection event for line item department field
   *
   * @param {ILineItem} objLineItem
   *
   * @returns {void}
   */
  onOpenDepartmentField(objLineItem: ILineItem): void {
    objLineItem._arSelectableDepartments$ = concat(of(this.arCachedDepartments), objLineItem._arSelectableDepartments$);
  }

  /**
   * Handles open selection event for line item account code field
   *
   * @param {ILineItem} objLineItem
   *
   * @returns {void}
   */
  onOpenAccountCodeField(objLineItem: ILineItem): void {
    objLineItem._arSelectableAccountCodes$ = concat(of(this.arCachedAccountCodes), objLineItem._arSelectableAccountCodes$);
  }

  /**
   * Handles open selection event for line item tax code field
   *
   * @param {ILineItem} objLineItem
   *
   * @returns {void}
   */
  onOpenTaxCodeField(objLineItem: ILineItem): void {
    objLineItem._arSelectableTaxCodes$ = concat(of(this.arCachedTaxCodes), objLineItem._arSelectableTaxCodes$);
  }

  /**
   * Add site text to reference when
   * a site is selected.
   *
   * @param {any} objRecord
   *
   * @returns {void}
   */
  onSiteChanged(objRecord: any): void {
    if (this.isCreateForm() && objRecord && objRecord.site_text) {
      this.objRecurringInvoice.strReference = objRecord.site_text;
    }
  }

   /**
   * change the due date when a
   * a customer is selected.
   *
   * @param {any} objRecord
   *
   * @returns {void}
   */
  onCustomerChanged(objRecord: any): void {
    if (objRecord && filled(objRecord['customer_payment_term_id'] && filled(objRecord['customer_invoice_terms_text']))){
      let objPaymentTerm = { id: objRecord['customer_payment_term_id'], text: objRecord['customer_invoice_terms_text'] };
      this.setCurrentPaymentTerm(objPaymentTerm);
    } else {
      this.setCurrentPaymentTerm(this.objDefaultPaymentTerm);
    }
  }

  /**
   * Triggers the form reference submit if present
   *
   * @returns {void}
   */
  triggerFormSubmit(): void {
    this.hasFormReference && this.recurringInvoiceForm.ngSubmit.emit();
  }

  /**
   * Listens to form error
   *
   * @returns {void}
   */
  onFormError(): void {
    this.disabledSaveButton$.next(true);
  }

  onLineTotalChange(input: HTMLInputElement, opts: {
    line: ILineItem,
  }): void {
    const adjustment = toFormattedNumber(input.value, {
      currency: true,
    });

    const quantity = toFormattedNumber(opts.line.quantity);
    const pricing = adjustment / quantity;

    opts.line.unitPrice = toFormattedNumber(pricing, {
      currency: true,
      maxDecimalPlaces: 4,
    });

    this.calculateLineItemsTotal();
  }

  onTaxAdjustment(input: HTMLInputElement): void {
    this.totalTaxAdjustment = toFormattedNumber(input.value, {
      currency: true,
    });

    this.calculateLineItemsTotal();
  }

  onGrandTotalChange(input: HTMLInputElement): void {
    const adjustment = toFormattedNumber(input.value, {
      currency: true,
    });

    const totalPrice = this.fltTotalExcludedTaxAmount;
    const totalTax = this.fltTotalTaxAmount;

    this.totalTaxAdjustment = toFormattedNumber((adjustment - (totalPrice + totalTax)), {
      currency: true,
    });
  }

  doSomethingFromContextMenu(event) {
    let copiedLineItemFromLocalStorage = this.contextMenuService.getCopiedLineItem();

    if (event.action == 'paste' && filled(copiedLineItemFromLocalStorage)) {
      let indexCounter = get(event, ['data', 'index']) + 1;
      copiedLineItemFromLocalStorage.forEach(lineItem => {
        this.addEmptyLineItem(lineItem);
        this.calculateAmount(this.arLineItems[indexCounter]);
        indexCounter++;
      });
    } else if (event.action == 'copy') {
      let copiedLineItems = [];
      let eventDataLineItem = get(event, ['data', 'line_item'], []);

      if (this.selectedLineItems.length > 0) {
        copiedLineItems = this.selectedLineItems.map(lineItem => {
          let newLineItem = this.formatCopiedLineItem(lineItem);

          return new RecurringInvoiceLine(newLineItem);
        });
      } else if (eventDataLineItem) {
        let newLineItem = this.formatCopiedLineItem(eventDataLineItem);

        copiedLineItems = [new RecurringInvoiceLine(newLineItem)];
      }

      if (filled(copiedLineItems)) {
        this.contextMenuService.setCopiedLineItem(copiedLineItems);
      }
    }
  }

  formatCopiedLineItem(lineItem: LooseObject) {
    let currentLineItem = cloneDeep(lineItem);
    currentLineItem['tax_code'] = get(currentLineItem, ['objTaxCode', 'code']);
    currentLineItem['tax_code_id'] = get(currentLineItem, ['objTaxCode', 'id']);
    currentLineItem['tax_code_name'] = get(currentLineItem, ['objTaxCode', 'name']);
    currentLineItem['tax_rate'] = get(currentLineItem, ['objTaxCode', 'rate']);
    currentLineItem['account_code'] = get(currentLineItem, ['objAccountCode', 'code']);
    currentLineItem['account_code_id'] = get(currentLineItem, ['objAccountCode', 'id']);
    currentLineItem['account_code_name'] = get(currentLineItem, ['objAccountCode', 'text']);
    currentLineItem['item_id'] = get(currentLineItem, ['objItem', 'id']);
    currentLineItem['item_code'] = get(currentLineItem, ['objItem', 'code']);
    currentLineItem['item_name'] = get(currentLineItem, ['objItem', 'text']);
    currentLineItem['labor'] = get(currentLineItem, ['objItem', 'labor']);
    currentLineItem['department_id'] = get(currentLineItem, ['objDepartment', 'id']);
    currentLineItem['department_name'] = get(currentLineItem, ['objDepartment', 'text']);

    return currentLineItem;
  }

  onClickedLineItem($event, attr) {
    this.selectedLineItems = this.contextMenuService.selectLineItem($event, attr, this.selectedLineItems);
  }

  /**
   * To open create item dialog form
   */
  createItem(lineItem: LooseObject): void {
    let itemData = this._compileItemDefaultValue(lineItem);
    this.dialog.open(EditformComponent, new FormPopup('items', {}, itemData))
      .afterClosed()
      .subscribe((response) => {
        if (get(response, 'status') == 'save' && filled(response.data)) {

          lineItem.objItem = filled(lineItem.objItem) ? lineItem.objItem : {};
          lineItem.objItem.id = response.data.id;
          lineItem.objItem.text = response.data.name;
          lineItem.objItem.code = response.data.code;
          lineItem.objItem.labor = response.data.labor;
          lineItem.unitPrice = toFormattedNumber(parseFloat(response.data.unit_price.toString()), {
            currency: true,
            maxDecimalPlaces: 4,
          });
          lineItem.amount = toFormattedNumber(parseFloat((lineItem.quantity * response.data.unit_price).toString()), {
            currency: true,
          });


        }
      });
  }

  /**
   * create default value for item dialog create
   *
   * @param lineItem
   * @returns
   */
  protected _compileItemDefaultValue(lineItem: LooseObject): LooseObject {
    let itemsDefaultValue: LooseObject = {
      name: get(lineItem, 'description'),
      code: get(lineItem, 'description'),
      pricing_method: 'fixed_sell_price',
      unit_price: get(lineItem, 'unitPrice'),
    };

    if (filled(lineItem.objTaxCode)) {
      itemsDefaultValue.default_tax_code_id = get(lineItem, 'objTaxCode.id');
      itemsDefaultValue.default_tax_code_text = get(lineItem, 'objTaxCode.name');
    }

    if (filled(lineItem.objAccountCode)) {
      itemsDefaultValue.default_account_code_id = get(lineItem, 'objAccountCode.id');
      itemsDefaultValue.default_account_code_text = get(lineItem, 'objAccountCode.name');
    }

    if (filled(this.objRecurringInvoice.objCustomer)) {
      itemsDefaultValue.customer_id = get(this.objRecurringInvoice.objCustomer, 'id');
      itemsDefaultValue.customer_text = get(this.objRecurringInvoice.objCustomer, 'text');
    }

    if (filled(this.objDefaultDepartment.id)) {
      itemsDefaultValue.department_id = get(lineItem, 'objDepartment.id');
      itemsDefaultValue.department_text = get(lineItem, 'objDepartment.text');
    }

    return itemsDefaultValue
  }

  /**
   * get the primary contact role to display in relate option
   *
   * @param roles
   */
  protected getPrimaryRole(roles): string | null {
    if (roles.length) {
        let primaryRole = roles.find( data => data.primary );
        return primaryRole ? primaryRole['id'] : roles[0]['id'];
    }
    return null;
  }

  protected initContractRelate(): void {
    let arContractOption: any = get(this.objRecurringInvoice.objContract, 'id')
      ? [ this.objRecurringInvoice.objContract ]
      : null
    this.objContractRelate.buildRelates(
      switchMap(strTerm => this.recordService.getRecordRelate('contracts', strTerm, '')),
      arContractOption,
      filled(arContractOption)
    );
  }

  private _computeDisplayedRole(roles: Record<string, any>[]): string {
    if (blank(roles)) {
      return 'no_role';
    }

    const primary = find(roles, (role) => get(role, 'primary', false)) || _first(roles);

    return get(primary, 'text', 'no_role');
  }

  setDefaultPaymentTerms(objRecordDetails){
    let objPaymentTerm = {
      id: objRecordDetails['customer_payment_term_id'] || (this.objDefaultPaymentTerm ? this.objDefaultPaymentTerm.id : null),
      text: objRecordDetails['customer_invoice_terms_text'] || (this.objDefaultPaymentTerm ? this.objDefaultPaymentTerm.text : '')
    };

    //set current payment term of the customer
    this.setCurrentPaymentTerm(objPaymentTerm);
  }
}

type RecurringInvoiceData = {
  /**
   * contains the unique identifier for the recurring invoice
   *
   * @var {string|null}
   */
  strId: string | null;

  /**
   * tells if the current recurring invoice is active
   *
   * @var {boolean}
   */
  isActive: boolean;

  /**
   * tells if the current recurring invoice is automatically
   * created via scheduler or automatic tasks handler
   *
   * @var {boolean}
   */
  isAutomatic: boolean;

  /**
   * indicates wether the generated customer invoice will be emailed
   * to the client
   *
   * @var {boolean}
   */
  emailToClient: boolean;

  /**
   * contains the current selected recurring invoice period data
   *
   * @var {boolean}
   */
  objPeriod: IPeriodOption;

  /**
   * contains the current datetime instance (moment) for the next invoice date
   *
   * @var {Moment}
   */
  objNextInvoiceDate: Moment;

  /**
   * contains the current selected customer data
   *
   * @var {ICustomerOption}
   */
  objCustomer: ICustomerOption;

  /**
   * contains the current selected contact data
   *
   * @var {IContactOption}
   */
  objContact: IContactOption;

  /**
   * contains the current selected site data
   *
   * @var {ISiteOption|null}
   */
  objSite?: ISiteOption | null;

  /**
   * contains the current datetime instance (moment) for the expiration date
   * of the recurring invoice
   *
   * @var {Moment|null}
   */
  objExpiryDate?: Moment | null;

  /**
   * contains the current purchase order number
   *
   * @var {string|null}
   */
  strPurchaseOrderNumber: string | null;

  /**
   * contains the currently selected date period for the invoice date
   *
   * @var {IDatePeriodOption}
   */
  objInvoiceDate: IDatePeriodOption;

  /**
   * contains the current invoice summary
   *
   * @var {string}
   */
  strInvoiceSummary: string;

  /**
   * contains the currently selected assigned user
   *
   * @var {IAssignedUserOption}
   */
  objAssignedUser: IAssignedUserOption;

  /**
   * Contains the invoice reference.
   *
   * @var {string}
   */
  strReference: string;

  /**
   * contains the current selected payment terms data
   *
   * @var {IPaymentTermOption|null}
   */
  objPaymentTerm?: IPaymentTermOption | null;

  /**
   *
   * @var {string}
   */

  repeated_every: string;

  /**
   * related contract
   */
  objContract?: LooseObject;
}